import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { bookingsApi } from '../../services/api';
import api from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Package, 
  Search,
  Filter,
  Eye,
  Loader2,
  FileText,
  Download,
  ClipboardList
} from 'lucide-react';
import { formatDate, formatCurrency, getStatusColor, attachmentFilename } from '../../lib/utils';
import { cargoLabel } from '../../components/booking/BulkFields';
import { EtaDisplay } from '../../components/shared/EtaDisplay';
import { toast } from 'sonner';

const UserBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [downloadingBL, setDownloadingBL] = useState(null);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      const response = await bookingsApi.getAll();
      setBookings(response.data);
    } catch (err) {
      console.error('Failed to load bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBL = async (bookingId, bookingNumber) => {
    setDownloadingBL(bookingId);
    try {
      const response = await api.get(`/bookings/${bookingId}/bill-of-lading`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachmentFilename(response, `BL_${bookingNumber}.pdf`));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Bill of Lading downloaded');
    } catch (err) {
      toast.error('Failed to generate Bill of Lading');
    } finally {
      setDownloadingBL(null);
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = booking.booking_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.origin_port.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.destination_port.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6" data-testid="user-bookings">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-[32px] sv-display">Bookings</h1>
          <p className="text-muted-foreground">Manage and track your shipments</p>
        </div>
        <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90">
          <Link to="/booking">New booking</Link>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by booking number or port..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="booking-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="in_transit">In transit</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-secondary" />
            </div>
          ) : filteredBookings.length > 0 ? (
            <div className="overflow-x-auto">
              <Table data-testid="bookings-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Booking #</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Container</TableHead>
                    <TableHead>Pickup date</TableHead>
                    <TableHead>ETA</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell className="sv-mono font-medium">
                        {booking.booking_number}
                        {booking.bl_number && <div className="text-xs font-normal text-muted-foreground" data-testid={"user-bl-" + booking.booking_number}>B/L {booking.bl_number}</div>}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{booking.origin_port.split(',')[0]}</p>
                          <p className="text-muted-foreground">→ {booking.destination_port.split(',')[0]}</p>
                        </div>
                      </TableCell>
                      <TableCell>{cargoLabel(booking)}</TableCell>
                      <TableCell>{formatDate(booking.pickup_date)}</TableCell>
                      <TableCell><EtaDisplay booking={booking} className="text-sm" testId={"user-eta-" + booking.booking_number} /></TableCell>
                      <TableCell>{formatCurrency(booking.price)}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(booking.status)}>
                          {booking.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {booking.payment_status !== 'paid' && booking.status === 'pending' && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              asChild
                              className="text-green-600 border-green-600 hover:bg-green-50"
                            >
                              <Link to={`/dashboard/payments/${booking.id}`}>
                                Pay Now
                              </Link>
                            </Button>
                          )}
                          {booking.status !== 'pending' && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDownloadBL(booking.id, booking.booking_number)}
                              disabled={downloadingBL === booking.id}
                              title="Download Bill of Lading"
                              data-testid={`download-bl-${booking.booking_number}`}
                            >
                              {downloadingBL === booking.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <FileText className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          {booking.status !== 'cancelled' && (
                            <Button variant="ghost" size="sm" asChild title={booking.shipping_instructions ? 'Shipping instructions submitted' : 'Complete shipping instructions for your bill of lading'} className={booking.shipping_instructions ? '' : 'text-amber-800 hover:text-amber-900'}>
                              <Link to={`/dashboard/bookings/${booking.id}/shipping-instructions`} data-testid={`si-${booking.booking_number}`}>
                                <ClipboardList className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" asChild title="Carrier documents">
                            <Link to="/dashboard/documents" state={{ bookingId: booking.id }} data-testid={`docs-${booking.booking_number}`}>
                              <Download className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/track?ref=${booking.booking_number}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-16">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No bookings found</p>
              <Button asChild>
                <Link to="/booking">Create your first booking</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserBookings;
